import type {
  ContentItem,
  GridContent,
} from "@/lib/contentParsers/contentItemSchemas";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import { ContentSectionType } from "@/types/contentSectionType";
import { isTableMetadataVariableType } from "@/types/metadataVariableType";

/**
 * Field paths under a grid's shared cell template (numeric content index).
 * Matches `content.{i}.cells.{j}.content.{k}.…` or singular `content.{i}.cell.content.{k}.…`.
 */
const DYNAMIC_GRID_CELL_TEMPLATE =
  /^content\.(\d+)\.(?:cells\.\d+\.content\.\d+|cell\.content\.\d+)(?:\.|$)/;

const isGridContent = (item: ContentItem): item is GridContent =>
  item.type === ContentSectionType.Grid;

export const isDynamicGridTemplateFieldPath = (fieldPath: string): boolean =>
  DYNAMIC_GRID_CELL_TEMPLATE.test(fieldPath);

/**
 * When the field sits inside a dynamic grid cell template (`contentFrom` names a metadata table),
 * return synthetic {@link Variables} rows—one per table column—so the RTE picker matches
 * {@link com.exagens.insight.blueprint.DynamicGridSectionDefinition} row substitution (column keys only).
 * Otherwise returns `metadataVariables` unchanged.
 */
export const richTextVariablesForFieldPath = (
  fieldPath: string,
  metadataVariables: Variables[],
  content: ContentItem[],
): Variables[] => {
  const match = DYNAMIC_GRID_CELL_TEMPLATE.exec(fieldPath);
  if (match == null) {
    return metadataVariables;
  }

  const sectionIndex = Number(match[1]);
  if (
    !Number.isFinite(sectionIndex) ||
    sectionIndex < 0 ||
    sectionIndex >= content.length
  ) {
    return metadataVariables;
  }

  const section = content[sectionIndex];
  if (!isGridContent(section)) {
    return metadataVariables;
  }

  const contentFrom =
    typeof section.contentFrom === "string" ? section.contentFrom.trim() : "";
  if (contentFrom === "") {
    return metadataVariables;
  }

  const tableVar = metadataVariables.find(
    (v) => v.name === contentFrom && isTableMetadataVariableType(v.type),
  );
  const columns = tableVar?.columns;
  if (columns == null || columns.length === 0) {
    return metadataVariables;
  }

  return columns.map((col) => ({
    name: col.name,
    type: col.type,
    columns: [],
    elements: [],
  }));
};
